import * as Dialog from '@radix-ui/react-dialog';
import { Loader2 } from 'lucide-react';
import { Checkbox } from '../../components/ui/Checkbox';
import { Tag } from '../../queries/tags';
import { SaleWindowFields } from './SaleWindowFields';
import { PackageTagRule, SubscriptionPackageDraft, TagRuleType } from '../../queries/subscriptionPackages';

interface SubscriptionPackageFormDialogProps {
  draft: SubscriptionPackageDraft;
  onChange: (draft: SubscriptionPackageDraft) => void;
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
  error: string | null;
  allTags: Tag[];
}

const inputClass = 'mt-1 w-full rounded-md border border-input bg-background px-2 py-2 text-sm';
const RULE_TYPE_CLASS: Record<TagRuleType, string> = {
  required: 'border-emerald-300 text-emerald-700 dark:border-emerald-800 dark:text-emerald-400',
  excluded: 'border-red-300 text-red-700 dark:border-red-800 dark:text-red-400',
  preferred: 'border-amber-300 text-amber-700 dark:border-amber-800 dark:text-amber-400',
};

export function SubscriptionPackageFormDialog({
  draft,
  onChange,
  onCancel,
  onSave,
  saving,
  error,
  allTags,
}: SubscriptionPackageFormDialogProps) {
  const isEdit = !!draft.id;
  const isOneTime = draft.billing_model === 'one_time';

  const tagRules = draft.tag_rules ?? [];
  const ruleForTag = (tagId: number) => tagRules.find((r) => r.tag_id === tagId)?.rule_type;
  const setRuleForTag = (tagId: number, ruleType: TagRuleType | 'none') => {
    const withoutTag = tagRules.filter((r) => r.tag_id !== tagId);
    if (ruleType === 'none') {
      onChange({ ...draft, tag_rules: withoutTag });
      return;
    }
    const newRule: PackageTagRule = { tag_id: tagId, rule_type: ruleType, is_default: false, tag_name: '', tag_category: '' };
    onChange({ ...draft, tag_rules: [...withoutTag, newRule] });
  };

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onCancel()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-full max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-border bg-background p-6 shadow-lg">
          <Dialog.Title className="text-lg font-semibold">{isEdit ? 'Edit package' : 'Add package'}</Dialog.Title>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <label className="text-sm">
              Name
              <input className={inputClass} value={draft.name ?? ''} onChange={(e) => onChange({ ...draft, name: e.target.value })} />
            </label>
            <label className="text-sm">
              Package code
              <input
                className={`${inputClass} font-mono`}
                value={draft.package_code ?? ''}
                onChange={(e) => onChange({ ...draft, package_code: e.target.value })}
                disabled={isEdit}
              />
            </label>
            <label className="col-span-2 text-sm">
              Description
              <input
                className={inputClass}
                value={draft.description ?? ''}
                onChange={(e) => onChange({ ...draft, description: e.target.value })}
              />
            </label>
            <label className="text-sm">
              Billing model
              <select
                className={inputClass}
                value={draft.billing_model ?? 'subscription'}
                onChange={(e) => {
                  const billing_model = e.target.value as 'subscription' | 'one_time';
                  // A fixed last day only applies to one-off packages and the sign-up trial; don't carry a hidden one over.
                  const keepsLastDay = billing_model === 'one_time' || draft.auto_assign_on_registration;
                  onChange({ ...draft, billing_model, ...(keepsLastDay ? {} : { access_ends_on: null }) });
                }}
              >
                <option value="subscription">Subscription</option>
                <option value="one_time">One-time</option>
              </select>
            </label>
            <label className="text-sm">
              Display order
              <input
                type="number"
                className={inputClass}
                value={draft.display_order ?? ''}
                onChange={(e) => onChange({ ...draft, display_order: e.target.value ? parseInt(e.target.value, 10) : null })}
              />
            </label>

            {isOneTime ? (
              <>
                <label className="text-sm">
                  One-time price ($)
                  <input
                    type="number"
                    step="0.01"
                    className={inputClass}
                    value={draft.price_one_time ?? ''}
                    onChange={(e) => onChange({ ...draft, price_one_time: e.target.value ? parseFloat(e.target.value) : null })}
                  />
                </label>
                <label className="text-sm">
                  Access duration (days)
                  <input
                    type="number"
                    className={inputClass}
                    value={draft.access_duration_days ?? ''}
                    onChange={(e) => onChange({ ...draft, access_duration_days: e.target.value ? parseInt(e.target.value, 10) : null })}
                  />
                </label>
                <label className="col-span-2 text-sm">
                  Access ends on (last day)
                  <input
                    type="date"
                    className={inputClass}
                    value={draft.access_ends_on ?? ''}
                    onChange={(e) => onChange({ ...draft, access_ends_on: e.target.value || null })}
                  />
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    Every buyer's access ends after this day, whenever they bought. Leave empty to use the access duration.
                  </span>
                </label>
              </>
            ) : (
              <>
                <label className="text-sm">
                  Weekly price ($)
                  <input
                    type="number"
                    step="0.01"
                    className={inputClass}
                    value={draft.price_weekly ?? ''}
                    onChange={(e) => onChange({ ...draft, price_weekly: e.target.value ? parseFloat(e.target.value) : null })}
                  />
                </label>
                <label className="text-sm">
                  Monthly price ($)
                  <input
                    type="number"
                    step="0.01"
                    className={inputClass}
                    value={draft.price_monthly ?? ''}
                    onChange={(e) => onChange({ ...draft, price_monthly: e.target.value ? parseFloat(e.target.value) : null })}
                  />
                </label>
                {draft.auto_assign_on_registration && (
                  <>
                    <label className="text-sm">
                      Trial length (days)
                      <input
                        type="number"
                        className={inputClass}
                        value={draft.access_duration_days ?? ''}
                        onChange={(e) => onChange({ ...draft, access_duration_days: e.target.value ? parseInt(e.target.value, 10) : null })}
                      />
                    </label>
                    <label className="text-sm">
                      Last day of any trial
                      <input
                        type="date"
                        className={inputClass}
                        value={draft.access_ends_on ?? ''}
                        onChange={(e) => onChange({ ...draft, access_ends_on: e.target.value || null })}
                      />
                      <span className="mt-0.5 block text-xs text-muted-foreground">
                        No trial runs past this day, and sign-ups after it get none. Leave empty for the full trial length.
                      </span>
                    </label>
                  </>
                )}
              </>
            )}

            <SaleWindowFields draft={draft} onChange={onChange} inputClass={inputClass} />

            {allTags.length > 0 && (
              <div className="col-span-2 border-t border-border pt-3">
                <h3 className="mb-2 text-sm font-medium">Track eligibility</h3>
                <p className="mb-2 text-xs text-muted-foreground">
                  Which tagged tracks this package includes/excludes. Leave "None" for tags that don't affect it.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {allTags.map((tag) => {
                    const current = ruleForTag(tag.id) ?? 'none';
                    return (
                      <label key={tag.id} className="flex items-center justify-between gap-2 text-sm">
                        <span>{tag.name}</span>
                        <select
                          className={`rounded-md border bg-background px-2 py-1 text-xs ${current !== 'none' ? RULE_TYPE_CLASS[current] : 'border-input'}`}
                          value={current}
                          onChange={(e) => setRuleForTag(tag.id, e.target.value as TagRuleType | 'none')}
                        >
                          <option value="none">None</option>
                          <option value="required">Required</option>
                          <option value="excluded">Excluded</option>
                          <option value="preferred">Preferred</option>
                        </select>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="col-span-2 space-y-3 pt-1">
              <PackageToggle
                label="Has Track Restrictions"
                badge={{ text: 'CRITICAL', className: 'bg-red-100 text-red-700' }}
                checked={!!draft.has_track_restrictions}
                onChange={(v) => onChange({ ...draft, has_track_restrictions: v })}
                help="MUST BE TRUE for packages that require users to select specific tracks (like “one_track”). When enabled, purchased tracks are recorded in the user_selected_tracks table. If disabled, users won't get access to their purchased tracks after payment."
              />
              <PackageToggle
                label="Allow Multiple Subscriptions"
                checked={!!draft.allow_multiple_subscriptions}
                onChange={(v) => onChange({ ...draft, allow_multiple_subscriptions: v })}
                help="When enabled, users can hold multiple instances of this package simultaneously. Useful for packages that allow stacking (e.g. multiple track selections)."
              />
              <PackageToggle
                label="Active"
                checked={draft.is_active !== false}
                onChange={(v) => onChange({ ...draft, is_active: v })}
                help="Controls whether this package is available for purchase. Inactive packages are hidden from users, but existing subscriptions continue to work."
              />
              <PackageToggle
                label="Private Package"
                badge={{ text: 'ADMIN ONLY', className: 'bg-slate-200 text-slate-700' }}
                checked={!!draft.is_private}
                onChange={(v) => onChange({ ...draft, is_private: v })}
                help="Private packages are only visible in admin interfaces for internal use (free months, friends/family, system-issue compensation). They won't appear in public package lists or user-facing APIs."
              />
              <PackageToggle
                label="Default for All Users"
                badge={{ text: 'PROMOTIONAL', className: 'bg-emerald-100 text-emerald-700' }}
                checked={!!draft.is_default_for_all_users}
                disabled={!draft.is_private}
                onChange={(v) => onChange({ ...draft, is_default_for_all_users: v })}
                help="Default packages are automatically provided to all users for free promotional access. Only private packages can be marked as default. Users see these with a “Complimentary Access” badge and cannot cancel them directly."
              />
              <PackageToggle
                label="Featured Promo Package"
                badge={{ text: 'HOMEPAGE', className: 'bg-amber-100 text-amber-700' }}
                checked={!!draft.is_promo_package}
                onChange={(v) => onChange({ ...draft, is_promo_package: v })}
                help="Promo packages are featured prominently on the logged-in home page with direct checkout. Only one promo package should be active at a time for the best presentation."
              />
              <PackageToggle
                label="Auto-assign on Registration"
                badge={{ text: 'ON SIGNUP', className: 'bg-blue-100 text-blue-700' }}
                checked={!!draft.auto_assign_on_registration}
                onChange={(v) => onChange({ ...draft, auto_assign_on_registration: v })}
                help="The free trial switch. This package is given to every new account at registration, and while it is on and active the sign-up pages, the public pages and the welcome email offer its free trial. Turn it off (or make the package inactive) and new accounts get no trial and nothing promises one. Only one package can have this enabled."
              />
              <PackageToggle
                label="Free Trial"
                badge={{ text: 'TRIAL', className: 'bg-emerald-100 text-emerald-700' }}
                checked={!!draft.is_trial}
                onChange={(v) => onChange({ ...draft, is_trial: v })}
                help="Marks this package as a free trial. Automated follow-ups use it to tell a trial that ran out from a paid membership that lapsed — so a package auto-assigned on signup that is NOT a trial (a permanent free tier, say) should leave this off."
              />
            </div>
            {error && <p className="col-span-2 text-sm text-destructive">{error}</p>}
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-md border border-input px-3 py-1.5 text-sm font-medium hover:bg-accent"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Save
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

interface PackageToggleProps {
  label: string;
  help: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  badge?: { text: string; className: string };
  disabled?: boolean;
}

function PackageToggle({ label, help, checked, onChange, badge, disabled }: PackageToggleProps) {
  return (
    <div className={disabled ? 'opacity-50' : ''}>
      <label className="flex items-center gap-2 text-sm font-medium">
        <Checkbox checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
        {label}
        {badge && <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${badge.className}`}>{badge.text}</span>}
      </label>
      <p className="ml-6 mt-0.5 text-xs text-muted-foreground">{help}</p>
    </div>
  );
}
